import {range} from 'lodash';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {carouselAnimationCurve} from './carouselCommon';

/*
 * Props.
 */

export interface CarouselBreadcrumbsProps {
  count: number;
  selectedIndex?: number;
  isHidden?: boolean;
  onDotClick?: HandlerOf<number>;
  isInfoBubble?: boolean;
}

/*
 * Style.
 */

interface BreadcrumbsStyleProps {
  $isInfoBubble?: boolean;
  $isHidden?: boolean;
}
const StyledBreadcrumbsDiv = styled.div<BreadcrumbsStyleProps>`
  ${(p) =>
    p.$isInfoBubble &&
    css`
      padding-top: 10px;
    `};

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 5px;

  opacity: ${(p) => (p.$isHidden ? 0 : 1)};
  transition: opacity 300ms ${carouselAnimationCurve};
`;

const StyledDotButton = styled(ChromelessButton)`
  padding: 2px;
`;

interface BreadcrumbDotStyleProps {
  $isSelected: boolean;
  $isInfoBubble?: boolean;
}

const StyledDotDiv = styled.div<BreadcrumbDotStyleProps>`
  height: ${(p) => (p.$isInfoBubble ? '8px' : '11px')};
  width: ${(p) => (p.$isInfoBubble ? '8px' : '11px')};

  border-radius: 50%;
  background-color: ${(p) => p.theme.greys.shade30};
  transition-property: background-color, transform;
  transition-timing-function: ${carouselAnimationCurve};
  transition-duration: 300ms;

  transform: scale(calc(10 / 11));

  ${(p) => maybeAddSelectedStyle(p)};
`;

function maybeAddSelectedStyle({$isSelected, $isInfoBubble}: BreadcrumbDotStyleProps) {
  if ($isSelected) {
    return css`
      background-color: ${(p) =>
        $isInfoBubble ? p.theme.palette.pink.shade40 : p.theme.palette.blue.shade40};
      transform: scale(1);
    `;
  }

  return css`
    background-color: ${(p) => ($isInfoBubble ? p.theme.greys.shade40 : p.theme.greys.shade30)};
  `;
}

/*
 * Component.
 */

export const CarouselBreadcrumbs: FC<CarouselBreadcrumbsProps> = (props) => {
  const {count, selectedIndex, onDotClick, isInfoBubble} = props;
  const dotRenders = range(0, count).map((index) => (
    <StyledDotButton key={index} onClick={() => onDotClick && onDotClick(index)}>
      <StyledDotDiv $isSelected={index === selectedIndex} $isInfoBubble={isInfoBubble} />
    </StyledDotButton>
  ));

  return (
    <StyledBreadcrumbsDiv $isInfoBubble={props.isInfoBubble} $isHidden={props.isHidden}>
      {dotRenders}
    </StyledBreadcrumbsDiv>
  );
};
